import { el, inject, create } from "@dhx/ts-common/dom";
import { uid } from "@dhx/ts-common/core";
import { Layout } from "@dhx/ts-layout";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { View } from "@dhx/ts-common/view";
import { RadioButton, RadioButtonEvents } from "./radiobutton";
import { getValidationMessage, getFormItemCss, isEmptyObj } from "../helper";
import { ValidationStatus, IRadioGroup, IBaseRadioGroupEventHandlersMap, BaseElementEvent } from "../types";


export class RadioGroup extends View {
	public config: IRadioGroup;
	public layout: Layout;
	public events: IEventSystem<BaseElementEvent, IBaseRadioGroupEventHandlersMap> = new EventSystem();
	private _buttons: RadioButton[] = [];

	constructor(container, config: IRadioGroup) {
		super(null, config);
		this.setConfig(config);

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	validate(): boolean {
		let isValid = false;
		this._buttons.map(element => {
			if (!this.config.required || element.config.checked) {
				isValid = true;
			}
		});
		this._buttons.map(element => {
			element.config.$validationStatus = isValid
				? ValidationStatus.success
				: ValidationStatus.error;
		});
		this.config.$validationStatus = isValid
			? ValidationStatus.success
			: ValidationStatus.error;
		this.paint();
		return isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this._buttons.map(element => {
			element.clearValidate();
		});
		this.paint();
	}
	clear(): void {
		this._buttons.map(element => {
			element.clear();
		});
		this.paint();
	}
	getValue(): string {
		this._buttons.map(element => {
			if (element.getValue()) {
				this.config.value = element.getValue() || "";
			}
		});
		return this.config.value || "";
	}
	setValue(value: string): void {
		this._buttons.map(element => {
			value === element.config.value
				? element.setValue(true)
				: element.setValue(false);
		});
		this.events.fire(BaseElementEvent.change);
		this.paint();
	}
	setConfig(config: IRadioGroup): void {
		if (isEmptyObj(config) || isEmptyObj(config.options)) {
			return;
		}
		if (this.layout) {
			this.layout.destructor();
		}
		if (this._buttons.length !== 0) {
			this._buttons.map(button => {
				button.destructor();
			});
			this._buttons = [];
		}
		this.config = {
			type: this.config.type,
			id: this.config.id,
			name: this.config.name,
			options: {}
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		const radioButtonsConfig = this.config.options.rows || this.config.options.cols;
		const { preMessage, errorMessage, successMessage } = this.config;
		radioButtonsConfig.map(option => {
			option.id = option.id || uid();
		});

		this.layout = new Layout(null, this.config.options);

		radioButtonsConfig.map(option => {
			const radiobutton = new RadioButton(null, option);
			radiobutton.config.disabled = config.disabled;
			radiobutton.config.name = config.name;
			radiobutton.config.required = config.required;
			radiobutton.config.css = config.css;

			if (preMessage || errorMessage || successMessage) {
				radiobutton.config.preMessage = "";
				radiobutton.config.errorMessage  = "";
				radiobutton.config.successMessage = "";
			}

			this._buttons.push(radiobutton);
			this.layout.getCell(option.id).attach(radiobutton);

			radiobutton.events.on(RadioButtonEvents.change, () => {
				this._buttons.map(button => {
					if(button.config.id !== radiobutton.config.id){
						button.setValue(false);
					}
				});
				this.events.fire(BaseElementEvent.change, []);
				this.validate();
			});
		});
		this.events.fire(BaseElementEvent.configUpdate, [this.config]);
		this.clearValidate();
	}
	private _draw() {
		const { labelWidth, labelInline } = this._buttons[0].config;
		const { hidden } = this.config;
		const visibility = hidden ? " dhx_form-group--hidden" : "";
		return el("div.dhx_form-group", {
			class: getFormItemCss(this.config, Boolean(this.config.required)) + visibility,
		}, [
			el("div.dhx_radio-group--container", {
			}, [
				inject(this.layout.getRootView()),
			]),
			el("div.dhx_caption--container", {
				style: {"margin-left": `${labelWidth && labelInline ? `calc(${labelWidth} + 16px)` : ""}`}
			}, [
				el("span.dhx_input__caption", getValidationMessage(this.config))
			])
		]);
	}
}